<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  mode: {
    type: String,
    default: 'single',
  },
});

const emit = defineEmits(['cancel', 'download', 'update:show']);
const { t } = useI18n();

const startDate = ref('');
const endDate = ref('');

const localShow = computed({
  get() {
    return props.show;
  },
  set(value) {
    emit('update:show', value);
  },
});

const title = computed(() =>
  props.mode === 'all'
    ? t('CONTACT_PANEL.PDF_EXPORT.TITLE_ALL')
    : t('CONTACT_PANEL.PDF_EXPORT.TITLE_SINGLE')
);

const hasInvalidRange = computed(
  () => startDate.value && endDate.value && startDate.value > endDate.value
);

const onCancel = () => {
  emit('cancel');
};

const onSubmit = () => {
  if (hasInvalidRange.value) return;

  emit('download', {
    startDate: startDate.value || null,
    endDate: endDate.value || null,
  });
};

watch(
  () => props.show,
  value => {
    if (!value) {
      startDate.value = '';
      endDate.value = '';
    }
  }
);
</script>

<template>
  <woot-modal v-model:show="localShow" :on-close="onCancel">
    <div class="flex flex-col h-auto overflow-auto">
      <woot-modal-header
        :header-title="title"
        :header-content="$t('CONTACT_PANEL.PDF_EXPORT.DESCRIPTION')"
      />
      <form class="w-full" @submit.prevent="onSubmit">
        <div class="grid w-full grid-cols-1 gap-4 sm:grid-cols-2">
          <label class="flex flex-col gap-1 text-sm text-n-slate-12">
            <span class="font-medium">
              {{ $t('CONTACT_PANEL.PDF_EXPORT.START_DATE') }}
            </span>
            <input
              v-model="startDate"
              type="date"
              class="w-full px-3 py-2 border rounded-md outline-none bg-n-alpha-2 border-n-weak text-n-slate-12"
            />
          </label>
          <label class="flex flex-col gap-1 text-sm text-n-slate-12">
            <span class="font-medium">
              {{ $t('CONTACT_PANEL.PDF_EXPORT.END_DATE') }}
            </span>
            <input
              v-model="endDate"
              type="date"
              class="w-full px-3 py-2 border rounded-md outline-none bg-n-alpha-2 border-n-weak text-n-slate-12"
            />
          </label>
        </div>
        <p class="mt-3 text-sm text-n-slate-11">
          {{ $t('CONTACT_PANEL.PDF_EXPORT.EMPTY_HINT') }}
        </p>
        <p v-if="hasInvalidRange" class="mt-2 text-sm text-red-500">
          {{ $t('CONTACT_PANEL.PDF_EXPORT.INVALID_RANGE') }}
        </p>
        <div class="flex flex-row justify-end w-full gap-2 px-0 py-2 mt-4">
          <NextButton
            faded
            slate
            type="reset"
            :label="$t('CONTACT_PANEL.PDF_EXPORT.CANCEL')"
            @click.prevent="onCancel"
          />
          <NextButton
            type="submit"
            :label="$t('CONTACT_PANEL.PDF_EXPORT.DOWNLOAD')"
            :disabled="hasInvalidRange"
          />
        </div>
      </form>
    </div>
  </woot-modal>
</template>
